import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { parseISO } from 'date-fns';
import { RootState } from '../../app/store';
import { apiRequest } from '../api/apiSlice';
import { CodeScanStatus, OrganizationReport, ReportResponse, ReportStatus } from './types';

export interface DashboardState {
  code: string | null;
  report: OrganizationReport | null;
  loading: boolean;
  codeScanStatus: CodeScanStatus | null;
}

const initialState: DashboardState = {
  code: null,
  report: null,
  loading: true,
  codeScanStatus: null,
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<DashboardState['loading']>) => {
      state.loading = action.payload;
    },
    setCodeScanStatus: (state, action: PayloadAction<DashboardState['codeScanStatus']>) => {
      state.codeScanStatus = action.payload;
    },
    setCode: (state, action: PayloadAction<DashboardState['code']>) => {
      state.code = action.payload;
    },
    setReport: (state, action: PayloadAction<DashboardState['report']>) => {
      state.report = action.payload;
    },
  },
});

export const { setLoading, setCodeScanStatus, setCode, setReport } = dashboardSlice.actions;

export const selectLoading = (state: RootState) => state.dashboard.loading;
export const selectCodeScanStatus = (state: RootState) => state.dashboard.codeScanStatus;
export const selectCode = (state: RootState) => state.dashboard.code;
export const selectReport = (state: RootState): DashboardState['report'] => {
  const report = state.dashboard.report;

  if (!report) {
    return null;
  }

  return {
    ...report,
    meta: {
      ...report.meta,
      createdAt: report.meta.createdAt instanceof Date ? report.meta.createdAt : parseISO(report.meta.createdAt),
    },
  };
};

const loadReport = createAsyncThunk<ReportResponse, string, { state: RootState }>(
  'dashboard/load-report',
  async (url, { dispatch }) => {
    const apiResult = await dispatch(apiRequest(async (http) => {
      return http.get(url);
    }));

    return (apiResult.payload as AxiosResponse<ReportResponse>).data;
  }
);

const loadCleanReport = createAsyncThunk<ReportResponse, string, { state: RootState }>(
  'dashboard/load-report-clean',
  async (url, { dispatch }) => {
    dispatch(setLoading(true));
    dispatch(setCodeScanStatus(null));

    return (await dispatch(loadReport(url))).payload as ReportResponse;
  }
);

const postReport = createAsyncThunk<ReportResponse, string, { state: RootState }>(
  'dashboard/post-report',
  async (url, { dispatch }) => {
    dispatch(setLoading(true));
    dispatch(setCodeScanStatus(null));

    const apiResult = await dispatch(apiRequest(async (http) => {
      return http.post(url);
    }));

    return (apiResult.payload as AxiosResponse<ReportResponse>).data;
  }
);

export const getLastReportCode = createAsyncThunk<string, never, { state: RootState }>(
  'dashboard/last-report',
  async (_, { dispatch }) => {
    const { payload: response } = await dispatch(loadCleanReport('report')) as { payload: ReportResponse };

    return response.code;
  }
);

export const requestNewReport = createAsyncThunk<void, never, { state: RootState }>(
  'dashboard/last-report',
  async (_, { dispatch }) => {
    await dispatch(postReport(`report`));
  }
);

export const checkReportStatus = createAsyncThunk<ReportStatus, string, { state: RootState }>(
  'dashboard/check-status',
  async (code, { dispatch }) => {
    const { payload: response } = await dispatch(loadCleanReport(`report/${code}/status`)) as { payload: ReportResponse };

    return response.status;
  }
);

export const checkCodeScanStatus = createAsyncThunk<CodeScanStatus, string, { state: RootState }>(
  'dashboard/check-code-scan-status',
  async (code, { dispatch }) => {
    const { payload: response } = await dispatch(loadReport(`report/${code}/status`)) as { payload: ReportResponse };

    return response.codeScanStatus;
  }
);

export const loadReportContent = createAsyncThunk<void, string, { state: RootState }>(
  'dashboard/load-content',
  async (code, { dispatch }) => {
    const { payload: response } = await dispatch(loadCleanReport(`report/${code}`)) as { payload: ReportResponse };

    dispatch(setLoading(false));
    dispatch(setCodeScanStatus(response.codeScanStatus));
    dispatch(setCode(response.code));
    dispatch(setReport(response.report));
  }
);

export default dashboardSlice.reducer;
